import { LocationFilter } from '@/shared/components/filters/LocationFilter';
import { ManufacturerFilter } from '@/shared/components/filters/ManufacturerFilter';
import { DateRangeFilter } from '@/shared/components/filters/DateRangeFilter';
import { OrderTypeFilter } from '@/shared/components/filters/OrderTypeFilter';
import { Button } from '@/shared/components/ui/Button';
import type { FrameFilters as FrameFiltersType, OrderType } from '@/shared/types/domain.types';

interface FrameFiltersProps {
  filters: FrameFiltersType;
  onFilterChange: (filters: FrameFiltersType) => void;
}

export function FrameFilters({ filters, onFilterChange }: FrameFiltersProps) {
  const handleLocationChange = (locationIds: string[]) => {
    onFilterChange({ ...filters, location_ids: locationIds });
  };

  const handleManufacturerChange = (manufacturerIds: string[]) => {
    onFilterChange({ ...filters, manufacturer_ids: manufacturerIds });
  };

  const handleOrderTypeChange = (orderType?: OrderType | 'ALL') => {
    onFilterChange({
      ...filters,
      order_type: orderType === 'ALL' ? undefined : orderType,
    });
  };

  const handleDateFromChange = (date: string) => {
    onFilterChange({ ...filters, date_from: date || undefined });
  };

  const handleDateToChange = (date: string) => {
    onFilterChange({ ...filters, date_to: date || undefined });
  };

  const handleClearFilters = () => {
    onFilterChange({
      location_ids: [],
      manufacturer_ids: [],
      status: ['PENDING'],
      order_type: undefined,
      date_from: undefined,
      date_to: undefined,
      search: undefined,
    });
  };

  const hasActiveFilters =
    (filters.location_ids?.length || 0) > 0 ||
    (filters.manufacturer_ids?.length || 0) > 0 ||
    filters.order_type !== undefined ||
    filters.date_from !== undefined ||
    filters.date_to !== undefined ||
    filters.search !== undefined;

  return (
    <div className="bg-white p-4 rounded-lg shadow space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <Button size="sm" variant="ghost" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <LocationFilter
          selectedLocationIds={filters.location_ids || []}
          onChange={handleLocationChange}
        />

        <ManufacturerFilter
          selectedManufacturerIds={filters.manufacturer_ids || []}
          onChange={handleManufacturerChange}
        />

        <OrderTypeFilter
          selectedOrderType={filters.order_type}
          onChange={handleOrderTypeChange}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search
          </label>
          <input
            type="text"
            placeholder="Brand, frame, patient..."
            value={filters.search || ''}
            onChange={(e) =>
              onFilterChange({ ...filters, search: e.target.value || undefined })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="pt-2 border-t">
        <DateRangeFilter
          dateFrom={filters.date_from}
          dateTo={filters.date_to}
          onDateFromChange={handleDateFromChange}
          onDateToChange={handleDateToChange}
        />
      </div>
    </div>
  );
}
